import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartTooltip, ResponsiveContainer, ReferenceArea,
} from 'recharts';
import {
  TrendingUp, TrendingDown, AlertTriangle, Lightbulb,
  ShoppingCart, Calendar, ChevronUp, ChevronDown,
} from 'lucide-react';
import { reportsAPI } from '@/lib/api';
import { toast } from 'sonner';
import FilterControls from '@/components/reports/FilterControls';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt  = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtK = (n) => {
  const v = Number(n || 0);
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  if (v >= 1000)   return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
};

const PERIOD_OPTIONS = [
  { label: '6 Months',  value: 6 },
  { label: '12 Months', value: 12 },
  { label: '24 Months', value: 24 },
];

const SEASON_CONFIG = {
  summer:  { label: 'Summer',  fill: '#fef3c7', stroke: '#f59e0b', text: 'text-amber-700',  bg: 'bg-amber-50',  border: 'border-amber-200',  dot: 'bg-amber-400'  },
  monsoon: { label: 'Monsoon', fill: '#eff6ff', stroke: '#3b82f6', text: 'text-blue-700',   bg: 'bg-blue-50',   border: 'border-blue-200',   dot: 'bg-blue-400'   },
  festive: { label: 'Festive', fill: '#fff7ed', stroke: '#f97316', text: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200', dot: 'bg-orange-400' },
  winter:  { label: 'Winter',  fill: '#f0f9ff', stroke: '#0ea5e9', text: 'text-sky-700',    bg: 'bg-sky-50',    border: 'border-sky-200',    dot: 'bg-sky-400'    },
};

const SEASON_ORDER = ['summer', 'monsoon', 'festive', 'winter'];

function Skeleton({ className }) {
  return <div className={`bg-muted rounded-xl animate-pulse ${className}`} />;
}

function PctBadge({ value, suffix = '%' }) {
  if (value == null) return null;
  const pos = value >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded-full ${
      pos ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'
    }`}>
      {pos ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      {pos ? '+' : ''}{value}{suffix}
    </span>
  );
}

// ── Section 1: Seasonal Pulse ─────────────────────────────────────────────────
function PulseTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const rev = payload.find(p => p.dataKey === 'revenue');
  const txn = payload.find(p => p.dataKey === 'transactions');
  return (
    <div className="bg-popover border border-border rounded-lg px-3 py-2 text-sm shadow-md">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {rev && <p className="text-emerald-600">{fmt(rev.value)}</p>}
      {txn && <p className="text-muted-foreground">{txn.value} bills</p>}
    </div>
  );
}

function SeasonalPulse({ data, loading }) {
  if (loading) return (
    <div className="bg-card rounded-xl border border-border p-5">
      <Skeleton className="h-5 w-40 mb-4" />
      <Skeleton className="h-56" />
    </div>
  );
  if (!data?.length) return null;

  // Build ReferenceArea groups: consecutive months of same season
  const bands = [];
  let cur = null;
  for (const m of data) {
    if (!cur || cur.season !== m.season) {
      if (cur) bands.push(cur);
      cur = { season: m.season, x1: m.label, x2: m.label };
    } else {
      cur.x2 = m.label;
    }
  }
  if (cur) bands.push(cur);

  return (
    <div className="bg-card rounded-xl border border-border p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-emerald-500" />
          <h3 className="text-sm font-semibold text-foreground">Seasonal Pulse</h3>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {SEASON_ORDER.map(s => {
            const cfg = SEASON_CONFIG[s];
            return (
              <span key={s} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className={`w-2.5 h-2.5 rounded-sm ${cfg.dot}`} />
                {cfg.label}
              </span>
            );
          })}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={data} margin={{ top: 4, right: 40, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor="#16a34a" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#16a34a" stopOpacity={0.0} />
            </linearGradient>
          </defs>
          {bands.map((b, i) => (
            <ReferenceArea
              key={i} x1={b.x1} x2={b.x2}
              fill={SEASON_CONFIG[b.season].fill}
              fillOpacity={0.6} stroke="none"
            />
          ))}
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
            axisLine={false} tickLine={false}
          />
          <YAxis
            yAxisId="rev" orientation="left"
            tickFormatter={fmtK}
            tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
            axisLine={false} tickLine={false} width={52}
          />
          <YAxis
            yAxisId="txn" orientation="right"
            tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
            axisLine={false} tickLine={false} width={36}
          />
          <RechartTooltip content={<PulseTooltip />} />
          <Area
            yAxisId="rev" type="monotone" dataKey="revenue"
            stroke="#16a34a" strokeWidth={2} fill="url(#revGrad)"
            dot={{ r: 3, fill: '#16a34a', strokeWidth: 0 }} activeDot={{ r: 5 }}
          />
          <Line
            yAxisId="txn" type="monotone" dataKey="transactions"
            stroke="#94a3b8" strokeWidth={1.5} dot={false}
            strokeDasharray="4 3"
          />
        </ComposedChart>
      </ResponsiveContainer>
      <p className="text-xs text-muted-foreground mt-2 text-right">
        Solid line = Revenue &nbsp;·&nbsp; Dashed line = Transactions
      </p>
    </div>
  );
}

// ── Section 2: Festival Impact ────────────────────────────────────────────────
function FestivalCard({ festival }) {
  const { is_upcoming, days_away, has_data, name, start, revenue, lift_pct,
          top_categories, category_hints, transactions } = festival;
  const urgent = is_upcoming && days_away != null && days_away <= 45;

  return (
    <div className={`min-w-[200px] max-w-[200px] rounded-xl border p-4 flex-shrink-0 ${
      urgent ? 'border-amber-300 bg-amber-50/60' : 'bg-card border-border'
    }`}>
      <div className="flex items-start justify-between mb-1 gap-1">
        <p className="text-sm font-semibold text-foreground leading-tight">{name}</p>
        {urgent && (
          <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap shrink-0">
            Prepare
          </span>
        )}
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        {is_upcoming ? `In ${days_away} days` : new Date(start + 'T00:00:00').toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })}
      </p>

      {has_data ? (
        <>
          <p className="text-base font-bold text-foreground">{fmt(revenue)}</p>
          <p className="text-xs text-muted-foreground mb-2">{transactions} bills</p>
          {lift_pct != null && <PctBadge value={lift_pct} suffix="% vs avg week" />}
          {top_categories?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {top_categories.slice(0, 2).map(c => (
                <span key={c} className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded-md">{c}</span>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="text-xs text-muted-foreground space-y-1">
          {is_upcoming ? (
            <>
              <p className="font-medium text-foreground">Typical stock-ups:</p>
              <div className="flex flex-wrap gap-1">
                {(category_hints || []).slice(0, 3).map(c => (
                  <span key={c} className="bg-muted px-1.5 py-0.5 rounded-md">{c}</span>
                ))}
              </div>
            </>
          ) : (
            <p className="italic">No data recorded</p>
          )}
        </div>
      )}
    </div>
  );
}

function FestivalImpact({ festivals, loading }) {
  if (loading) return (
    <div className="bg-card rounded-xl border border-border p-5">
      <Skeleton className="h-5 w-44 mb-4" />
      <div className="flex gap-3">
        {[1,2,3,4].map(i => <Skeleton key={i} className="h-36 w-48 flex-shrink-0" />)}
      </div>
    </div>
  );
  if (!festivals?.length) return null;

  return (
    <div className="bg-card rounded-xl border border-border p-5">
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="h-4 w-4 text-orange-500" />
        <h3 className="text-sm font-semibold text-foreground">Festival Impact</h3>
        <span className="text-xs text-muted-foreground ml-1">
          — upcoming first, then recent history
        </span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin">
        {festivals.map((f, i) => <FestivalCard key={`${f.name}-${f.start}`} festival={f} />)}
      </div>
    </div>
  );
}

// ── Section 3: Category Heatmap ───────────────────────────────────────────────
function CategoryHeatmap({ categoryMonthly, loading }) {
  const [tooltip, setTooltip] = useState(null);
  const containerRef = useRef(null);

  const categories = Object.keys(categoryMonthly || {});
  const months     = categories.length > 0 ? (categoryMonthly[categories[0]] || []) : [];

  const heatColor = (index) => {
    if (!index || index === 0) return 'rgba(22,163,74,0.04)';
    const capped = Math.min(index, 2.5);
    const opacity = (capped / 2.5) * 0.85;
    return `rgba(22,163,74,${opacity.toFixed(2)})`;
  };

  const textColor = (index) => {
    if (!index || index < 0.5) return '#94a3b8';
    return index > 1.3 ? '#14532d' : '#374151';
  };

  if (loading) return (
    <div className="bg-card rounded-xl border border-border p-5">
      <Skeleton className="h-5 w-48 mb-4" />
      <Skeleton className="h-64" />
    </div>
  );
  if (!categories.length) return null;

  return (
    <div className="bg-card rounded-xl border border-border p-5 relative" ref={containerRef}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-foreground">Category Seasonality Heatmap</h3>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-sm" style={{ background: heatColor(0.2) }} />
            Below avg
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-sm" style={{ background: heatColor(1.0) }} />
            Average
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-sm" style={{ background: heatColor(2.5) }} />
            2.5× avg
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <div style={{ minWidth: Math.max(months.length * 52 + 140, 400) }}>
          {/* Header row */}
          <div className="flex mb-1">
            <div className="w-36 shrink-0" />
            {months.map(m => (
              <div key={m.month} className="flex-1 text-center text-[10px] text-muted-foreground font-medium px-0.5 min-w-[44px]">
                {m.label}
              </div>
            ))}
          </div>
          {/* Data rows */}
          {categories.map(cat => (
            <div key={cat} className="flex items-center mb-0.5">
              <div className="w-36 shrink-0 text-xs text-muted-foreground pr-2 truncate" title={cat}>
                {cat}
              </div>
              {(categoryMonthly[cat] || []).map(cell => (
                <div
                  key={cell.month}
                  className="flex-1 h-8 mx-0.5 rounded-sm cursor-default flex items-center justify-center min-w-[44px]"
                  style={{ background: heatColor(cell.index) }}
                  onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTooltip({ cat, cell, top: rect.top, left: rect.left + rect.width / 2 });
                  }}
                  onMouseLeave={() => setTooltip(null)}
                >
                  {cell.index > 0 && (
                    <span className="text-[9px] font-semibold" style={{ color: textColor(cell.index) }}>
                      {cell.index.toFixed(1)}×
                    </span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      {tooltip && (
        <div
          className="fixed z-50 bg-popover border border-border rounded-lg px-3 py-2 text-sm shadow-lg pointer-events-none"
          style={{ top: tooltip.top - 72, left: tooltip.left - 80 }}
        >
          <p className="font-semibold text-foreground">{tooltip.cat} — {tooltip.cell.label}</p>
          <p className="text-emerald-600">{fmt(tooltip.cell.revenue)}</p>
          <p className="text-muted-foreground">{tooltip.cell.units} units</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {tooltip.cell.index > 0
              ? `${tooltip.cell.index}× this category's monthly average`
              : 'No sales this month'}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Section 4 Left: Products by Season ───────────────────────────────────────
function ProductsBySeason({ seasonalProducts, loading }) {
  const [activeSeason, setActiveSeason] = useState('festive');

  if (loading) return (
    <div className="bg-card rounded-xl border border-border p-5">
      <Skeleton className="h-5 w-44 mb-4" />
      <Skeleton className="h-48" />
    </div>
  );

  const products = seasonalProducts?.[activeSeason] || [];

  return (
    <div className="bg-card rounded-xl border border-border p-5">
      <div className="flex items-center gap-2 mb-3">
        <ShoppingCart className="h-4 w-4 text-violet-500" />
        <h3 className="text-sm font-semibold text-foreground">Top Products by Season</h3>
      </div>
      <div className="flex gap-1 mb-4 p-1 bg-muted rounded-lg w-fit">
        {SEASON_ORDER.map(s => {
          const cfg = SEASON_CONFIG[s];
          return (
            <button
              key={s}
              onClick={() => setActiveSeason(s)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                activeSeason === s
                  ? `bg-card text-foreground shadow-sm`
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {cfg.label}
            </button>
          );
        })}
      </div>
      {products.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground text-sm">
          No sales recorded in {SEASON_CONFIG[activeSeason].label} months yet
        </div>
      ) : (
        <div className="space-y-1.5">
          {products.map((p, i) => (
            <div key={i} className="flex items-center gap-3 py-1.5 border-b border-border last:border-0">
              <span className="text-xs text-muted-foreground w-5 text-right shrink-0">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{p.name}</p>
                <p className="text-xs text-muted-foreground">{p.category}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-semibold text-foreground">{p.units} units</p>
                <p className="text-xs text-muted-foreground">{fmt(p.revenue)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Section 4 Right: Preparation Alerts ───────────────────────────────────────
function PreparationAlerts({ alerts, loading }) {
  if (loading) return (
    <div className="bg-card rounded-xl border border-border p-5">
      <Skeleton className="h-5 w-44 mb-4" />
      <div className="space-y-3">
        {[1,2,3].map(i => <Skeleton key={i} className="h-20" />)}
      </div>
    </div>
  );

  return (
    <div className="bg-card rounded-xl border border-border p-5">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        <h3 className="text-sm font-semibold text-foreground">Stock Preparation Alerts</h3>
        <span className="text-xs text-muted-foreground">— next 90 days</span>
      </div>
      {!alerts?.length ? (
        <div className="text-center py-8 text-muted-foreground text-sm">
          No major festivals in the next 90 days
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map((a, i) => (
            <div key={i} className={`rounded-lg border p-3.5 ${
              a.days_away <= 14
                ? 'border-red-200 bg-red-50/50'
                : a.days_away <= 45
                ? 'border-amber-200 bg-amber-50/40'
                : 'border-border bg-muted/30'
            }`}>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-sm font-semibold text-foreground">{a.event}</p>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  a.days_away <= 14
                    ? 'bg-red-100 text-red-700'
                    : a.days_away <= 45
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {a.days_away}d away
                </span>
              </div>
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground mb-1">Stock up on:</p>
                  <div className="flex flex-wrap gap-1">
                    {(a.top_categories || []).map(c => (
                      <span key={c} className="text-xs bg-background border border-border px-1.5 py-0.5 rounded-md font-medium text-foreground">
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
                {a.lift_pct != null && (
                  <div className="text-right shrink-0">
                    <p className="text-[10px] text-muted-foreground">Last year</p>
                    <PctBadge value={a.lift_pct} suffix="% lift" />
                  </div>
                )}
              </div>
              {!a.has_data && (
                <p className="text-[10px] text-muted-foreground mt-1.5 italic">
                  Based on typical kirana patterns — no prior year data yet
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Section 5: Year-over-Year ─────────────────────────────────────────────────
function YoYPanel({ yoy, loading }) {
  if (loading || !yoy) return null;

  const kpis = [
    { label: 'Revenue', value: yoy.revenue_change_pct, curr: fmt(yoy.current_revenue), prev: fmt(yoy.prev_revenue) },
    { label: 'Transactions', value: yoy.txn_change_pct, curr: `${yoy.current_txn}`, prev: `${yoy.prev_txn}` },
    { label: 'Avg Bill', value: yoy.current_avg_bill && yoy.prev_avg_bill
      ? Math.round((yoy.current_avg_bill / yoy.prev_avg_bill - 1) * 100)
      : null,
      curr: fmt(yoy.current_avg_bill), prev: fmt(yoy.prev_avg_bill) },
  ];

  return (
    <div className="bg-card rounded-xl border border-border p-5">
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="h-4 w-4 text-blue-500" />
        <h3 className="text-sm font-semibold text-foreground">Year-over-Year Comparison</h3>
        <span className="text-xs text-muted-foreground">— last 6 months vs same period last year</span>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-5">
        {kpis.map(k => (
          <div key={k.label} className="bg-muted/40 rounded-lg p-3 text-center">
            <p className="text-xs text-muted-foreground mb-1">{k.label}</p>
            <p className="text-base font-bold text-foreground">{k.curr}</p>
            <div className="flex items-center justify-center gap-1.5 mt-1">
              <PctBadge value={k.value} />
              <span className="text-xs text-muted-foreground">vs {k.prev}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {yoy.top_growth?.length > 0 && (
          <div>
            <p className="text-xs font-medium text-emerald-700 mb-2 flex items-center gap-1">
              <TrendingUp className="h-3 w-3" /> Top growing categories
            </p>
            <div className="space-y-1.5">
              {yoy.top_growth.map(c => (
                <div key={c.category} className="flex items-center justify-between text-sm">
                  <span className="text-foreground truncate">{c.category}</span>
                  <PctBadge value={c.change_pct} />
                </div>
              ))}
            </div>
          </div>
        )}
        {yoy.top_decline?.length > 0 && (
          <div>
            <p className="text-xs font-medium text-red-600 mb-2 flex items-center gap-1">
              <TrendingDown className="h-3 w-3" /> Categories to watch
            </p>
            <div className="space-y-1.5">
              {yoy.top_decline.map(c => (
                <div key={c.category} className="flex items-center justify-between text-sm">
                  <span className="text-foreground truncate">{c.category}</span>
                  <PctBadge value={c.change_pct} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Section 6: Insights ────────────────────────────────────────────────────────
function InsightsStrip({ insights, loading }) {
  if (loading) return (
    <div className="bg-card rounded-xl border border-border p-5">
      <Skeleton className="h-5 w-36 mb-3" />
      <div className="space-y-2">
        {[1,2,3].map(i => <Skeleton key={i} className="h-10" />)}
      </div>
    </div>
  );
  if (!insights?.length) return null;

  return (
    <div className="bg-card rounded-xl border border-border p-5">
      <div className="flex items-center gap-2 mb-3">
        <Lightbulb className="h-4 w-4 text-amber-500" />
        <h3 className="text-sm font-semibold text-foreground">Seasonal Insights</h3>
      </div>
      <ul className="space-y-2.5">
        {insights.map((insight, i) => (
          <li key={i} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
            <span className="mt-0.5 w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center shrink-0">
              {i + 1}
            </span>
            <p className="text-sm text-foreground leading-relaxed">{insight}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Main Tab Component ─────────────────────────────────────────────────────────
export default function SeasonalityTab() {
  const [months, setMonths]   = useState(12);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [categoryFilter, setCategoryFilter]   = useState('All');

  const load = useCallback(async (m, catalogId) => {
    setLoading(true);
    try {
      const res = await reportsAPI.getSeasonality(m, catalogId);
      setData(res.data);
    } catch {
      toast.error('Failed to load seasonality data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(months); }, [months, load]);

  const handleProductSelect = useCallback((product) => {
    setSelectedProduct(product);
    if (product.category) setCategoryFilter(product.category);
    load(months, product.catalog_id);
  }, [months, load]);

  const handleProductClear = useCallback(() => {
    setSelectedProduct(null);
    setCategoryFilter('All');
    load(months, undefined);
  }, [months, load]);

  const categoryOptions = Object.keys(data?.category_monthly || {});

  const effectiveCategoryFilter = selectedProduct?.category || categoryFilter;

  const categoryMonthlyFiltered = effectiveCategoryFilter === 'All'
    ? data?.category_monthly
    : data?.category_monthly?.[effectiveCategoryFilter]
      ? { [effectiveCategoryFilter]: data.category_monthly[effectiveCategoryFilter] }
      : {};

  const seasonalProductsFiltered = Object.fromEntries(
    Object.entries(data?.seasonal_products || {}).map(([season, products]) => [
      season,
      (products || []).filter(p => {
        const catMatch = effectiveCategoryFilter === 'All' || p.category === effectiveCategoryFilter;
        const nameMatch = !selectedProduct || p.name === (selectedProduct.name || selectedProduct.product_name);
        return catMatch && nameMatch;
      }),
    ])
  );

  return (
    <div className="space-y-6">
      {/* Header + Period Selector + Filters */}
      <div className="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground">
          Understand your sales cycles, festival peaks, and category patterns — gets richer with more data.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 p-1 bg-muted rounded-lg shrink-0">
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setMonths(opt.value)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  months === opt.value
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <FilterControls
            selectedProduct={selectedProduct}
            onProductSelect={handleProductSelect}
            onProductClear={handleProductClear}
            categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter}
            categoryOptions={categoryOptions}
          />
        </div>
      </div>

      {/* 1. Seasonal Pulse */}
      <SeasonalPulse data={data?.monthly_trend} loading={loading} />

      {/* 2. Festival Impact */}
      <FestivalImpact festivals={data?.festival_windows} loading={loading} />

      {/* 3. Category Heatmap */}
      <CategoryHeatmap categoryMonthly={categoryMonthlyFiltered} loading={loading} />

      {/* 4. Products by Season + Preparation Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ProductsBySeason seasonalProducts={seasonalProductsFiltered} loading={loading} />
        <PreparationAlerts alerts={data?.preparation_alerts} loading={loading} />
      </div>

      {/* 5. YoY Panel (conditional) */}
      {data?.has_yoy_data && <YoYPanel yoy={data?.yoy} loading={loading} />}

      {/* 6. Insights */}
      <InsightsStrip insights={data?.insights} loading={loading} />
    </div>
  );
}
